import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  AttachDirectCostReceiptInputData,
  CreateOrUpdateProcoreDirectCostInputData,
  CreateReimbursementDirectCostInputData,
  GetOrRefreshProcoreAccessTokenInputData,
  GetProcoreCompaniesInputData,
  GetProcoreCompaniesPayload,
  GetProcoreProjectUsersInputData,
  GetProcoreProjectUsersPayload,
  GetProcoreProjectsInputData,
  GetProcoreProjectsPayload,
  GetProcoreWBSCodesInputData,
  GetProcoreWBSCodesPayload,
  procoreService,
} from 'app/services/procore.service';
import { RootState } from 'app/store/rootReducer';

export const PROCORE_TOKENS_KEY = 'PROCORE_TOKENS';

export interface ProcoreState {
  procoreLoading: boolean;
  companies: GetProcoreCompaniesPayload[];
  selectedCompany?: GetProcoreCompaniesPayload;
  procoreProjects: GetProcoreProjectsPayload[];
  projectWbsCodes: Record<number, GetProcoreWBSCodesPayload[]>;
  projectUsers: GetProcoreProjectUsersPayload[];
}

const initialState: ProcoreState = {
  procoreLoading: false,
  companies: [],
  selectedCompany: undefined,
  procoreProjects: [],
  projectWbsCodes: {},
  projectUsers: [],
};

export const getOrRefreshAccessToken = createAsyncThunk(
  'procore/getOrRefreshAccessToken',
  async (data: GetOrRefreshProcoreAccessTokenInputData, { rejectWithValue }) => {
    return procoreService
      .getOrRefreshProcoreAccessToken(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const getCompanies = createAsyncThunk(
  'procore/getCompanies',
  async (data: GetProcoreCompaniesInputData, { rejectWithValue }) => {
    return procoreService
      .getProcoreCompanies(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const getProjects = createAsyncThunk(
  'procore/getProjects',
  async (data: GetProcoreProjectsInputData, { rejectWithValue }) => {
    return procoreService
      .getProcoreProjects(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const getProjectUsers = createAsyncThunk(
  'procore/getProjectUsers',
  async (data: GetProcoreProjectUsersInputData, { rejectWithValue }) => {
    return procoreService
      .getProcoreProjectUsers(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const getWbsCodes = createAsyncThunk(
  'procore/getWBSCodes',
  async (data: GetProcoreWBSCodesInputData, { rejectWithValue }) => {
    return procoreService
      .getProcoreWBSCodes(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const createOrUpdateDirectCost = createAsyncThunk(
  'procore/createOrUpdateDirectCost',
  async (data: CreateOrUpdateProcoreDirectCostInputData, { rejectWithValue }) => {
    return procoreService
      .createOrUpdateProcoreDirectCost(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const createReimbursementProcoreDirectCost = createAsyncThunk(
  'procore/createReimbursementDirectCost',
  async (data: CreateReimbursementDirectCostInputData, { rejectWithValue }) => {
    return procoreService
      .createReimbursementDirectCost(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

export const attachProcoreDirectCostReceipt = createAsyncThunk(
  'procore/attachDirectCostReceipt',
  async (data: AttachDirectCostReceiptInputData, { rejectWithValue }) => {
    return procoreService
      .attachDirectCostReceipt(data)
      .catch((error: Error) => rejectWithValue([{ message: error.message }]));
  }
);

const procoreSlice = createSlice({
  name: 'procore',
  initialState,
  reducers: {
    resetProcoreSlice: () => initialState,
    setSelectedCompany: (state, action) => {
      state.selectedCompany = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCompanies.fulfilled, (state, action) => {
      state.companies = action.payload || [];

      if (state.companies.length === 0) {
        state.selectedCompany = undefined;
      } else if (state.companies.length > 0 && !state.selectedCompany) {
        state.selectedCompany = state.companies[0];
      }
    });

    builder.addCase(getProjects.fulfilled, (state, action) => {
      state.procoreProjects = action.payload || [];
    });

    builder.addCase(getProjectUsers.fulfilled, (state, action) => {
      state.projectUsers = action.payload || [];
    });

    builder.addCase(getWbsCodes.fulfilled, (state, action) => {
      state.projectWbsCodes[action.meta.arg.projectId] = action.payload || [];
    });

    builder.addMatcher(
      isAnyOf(
        getCompanies.pending,
        getProjects.pending,
        getWbsCodes.pending,
        createOrUpdateDirectCost.pending
      ),
      (state) => {
        state.procoreLoading = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getCompanies.fulfilled,
        getCompanies.rejected,
        getProjects.fulfilled,
        getProjects.rejected,
        getProjectUsers.fulfilled,
        getProjectUsers.rejected,
        getWbsCodes.fulfilled,
        getWbsCodes.rejected,
        createOrUpdateDirectCost.fulfilled,
        createOrUpdateDirectCost.rejected
      ),
      (state) => {
        state.procoreLoading = false;
      }
    );
  },
});

export const { resetProcoreSlice, setSelectedCompany } = procoreSlice.actions;

export const selectProcoreSlice = (state: RootState): ProcoreState => state?.cards?.procore ?? initialState;

export default procoreSlice.reducer;
